import { postV2WorkspacesWorkspaceidFleetsMutationRequestDeploymentStrategyEnum } from '@emma/api_client/types';
import {
  Box,
  Button,
  Flex,
  FormControl,
  HorizontalStack,
  Input,
  Modal,
  Text,
  Textarea,
} from '@lego/klik-ui';
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { z } from 'zod';
import { DocumentationLink } from '@/components/common/DocumentationLink';
import { getApiErrorDetail } from '@/hooks/api';
import { useCreateFleet } from '@/hooks/fleet';
import { deploymentStrategyDocURL } from '@/utility/external_urls';
import { paths } from '@/utility/global_paths';
import { deploymentStrategySchema, fleetNameSchema } from '@/validation/fleet';

const NAME_MAX_LENGTH = 100;

const STRATEGY_OPTIONS = Object.values(
  postV2WorkspacesWorkspaceidFleetsMutationRequestDeploymentStrategyEnum
);

const selectProps = {
  as: 'select' as const,
  borderColor: 'gray.300',
  borderRadius: 'md',
  borderWidth: '1px',
  p: 2,
  width: '100%',
};

type Props = {
  workspaceId: number;
  isOpen: boolean;
  onClose: () => void;
};

export const CreateFleetDialog = ({ workspaceId, isOpen, onClose }: Props) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deploymentStrategy, setDeploymentStrategy] = useState<
    z.infer<typeof deploymentStrategySchema> | ''
  >('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const navigate = useNavigate();

  const lastErrorDetail = useRef<string | undefined>(undefined);
  const onApiError = useCallback((err?: unknown) => {
    lastErrorDetail.current = getApiErrorDetail(err);
  }, []);
  const createFleet = useCreateFleet(workspaceId, onApiError);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setDeploymentStrategy('');
      setError(undefined);
    }
  }, [isOpen]);

  const trimmedName = name.trim();
  const nameValidation = fleetNameSchema.safeParse(trimmedName);
  const nameIsInvalid = trimmedName.length > 0 && !nameValidation.success;
  const canSubmit = nameValidation.success && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(undefined);
    lastErrorDetail.current = undefined;
    try {
      const result = await createFleet({
        name: trimmedName,
        description: description.trim() || null,
        ...(deploymentStrategy ? { deploymentStrategy } : {}),
      });
      onClose();
      navigate(paths.fleetById(workspaceId, result.id));
    } catch {
      setError(lastErrorDetail.current ?? 'Failed to create fleet');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Create Fleet</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <FormControl isInvalid={nameIsInvalid} isRequired>
            <FormControl.Label>Name</FormControl.Label>
            <Input
              maxLength={NAME_MAX_LENGTH}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void handleSubmit();
                }
              }}
              placeholder="Fleet name (lowercase, numbers, dots, underscores)"
              value={name}
            />
            {nameIsInvalid && (
              <FormControl.ErrorText>
                {nameValidation.error?.issues[0]?.message}
              </FormControl.ErrorText>
            )}
          </FormControl>
          <FormControl mt={4}>
            <FormControl.Label>Description</FormControl.Label>
            <Textarea
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              placeholder="Optional description"
              value={description}
            />
          </FormControl>
          <FormControl mt={4}>
            <Flex align="center" direction="row" gap={1}>
              <FormControl.Label mb={0}>Deployment Strategy</FormControl.Label>
              <DocumentationLink url={deploymentStrategyDocURL} />
            </Flex>
            <Box
              {...selectProps}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                const parsed = deploymentStrategySchema.safeParse(e.target.value);
                setDeploymentStrategy(parsed.success ? parsed.data : '');
              }}
              value={deploymentStrategy}
            >
              <option value="">Select strategy (optional)</option>
              {STRATEGY_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Box>
          </FormControl>
          {error && (
            <Text color="error.500" fontSize="sm" mt={4}>
              {error}
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <HorizontalStack spacing={2}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button isDisabled={!canSubmit} isLoading={isSubmitting} onClick={handleSubmit}>
              Create
            </Button>
          </HorizontalStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
